import { SelectChangeEvent } from '@mui/material';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { CountZoneDto, Item, ItemModel, PrimaryCategoryStockCount, StockCheckSchedule, StockCheckScheduleCategory, StockCheckScheduleCountZone, StockCheckScheduleDetail, StockCheckScheduleItem, StockCheckTypesResponse, Store2 } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { InternalStockCheckSchedule } from '../interfaces/StockInterfaces';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import dayjs from 'dayjs';
import { hasUnassignedZone } from '../../../utils';

export const useScheduleCreate = () => {
  //use hooks
  const { t } = useTranslation('createStockCountSchedule');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getConfiguration, generatestockchecks, getStockCheckScheduleByNumber, stockcheckschedulePOST, getCountZoneDetails, getStockCheckTypes } = useNswagClient();
  const navigate = useNavigate();
  const { logError } = useLogError();
  //consts
  const today = dayjs().format('YYYY-MM-DD');

  //modal open state
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isProductTypeModalOpen, setIsProductTypeModalOpen] = useState(false);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] = useState(false);
  const [isScheduleDeleteModalOpen, setIsScheduleDeleteModalOpen] = useState(false);
  const [isCountZonesModalOpen, setIsCountZonesModalOpen] = useState(false);
  const [isCountZonesDeleteModalOpen, setIsCountZonesDeleteModalOpen] = useState(false);
  const [isCountZonesWithItemsModalOpen, setisCountZonesWithItemsModalOpen] = useState(false);
  const [isProductTypeDeleteModalOpen, setIsProductTypeDeleteModalOpen] = useState(false);
  const [isEndOfMonthCountModalOpen, setIsEndOfMonthCountModalOpen] = useState(false);
  const [existingStockCheckStatus, setExistingStockCheckStatus] = useState<number | null>(null);
  const [existingStockCheckId, setExistingStockCheckId] = useState<number | null>(null);

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  //other state
  const [categoryData, setCategoryData] = useState<StockCheckScheduleCategory[]>([]);
  const [productTypeData, setProductTypeData] = useState<string[]>([]);
  const [itemsData, setItemsData] = useState<StockCheckScheduleItem[]>([]);
  const [schedules, setSchedules] = useState<InternalStockCheckSchedule[]>([]);
  const [countZonesData, setCountZonesData] = useState<StockCheckScheduleCountZone[]>([]);
  const [stores, setStores] = useState<Store2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCreated, setHasCreated] = useState(false);
  const [countZoneItemsData, setcountZoneItemsData] = useState<ItemModel[]>([]);
  const [strictEmailApprovalEnabled, setStrictEmailApprovalEnabled] = useState<boolean | null>(null);

  //row delete ids
  const [categoryNameToDelete, setCategoryNameToDelete] = useState<string>();
  const [productType, setProductType] = useState<string>('');
  const [itemNumberToDelete, setItemNumberToDelete] = useState<string>();
  const [scheduleIds, setScheduleIds] = useState<number[]>();
  const [scheduleToDeleteIndex, setScheduleToDeleteIndex] = useState<number | undefined>();
  const [deleteAllSchedulesFlag, setDeleteAllSchedulesFlag] = useState<boolean>(false);
  const [countZoneIdToDelete, setCountZoneIdToDelete] = useState<number | undefined>();

  //main box values
  const [name, setName] = useState('');
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [approvalValue, setApprovalValue] = useState<number | undefined>();
  const [selectedTime, setSelectedTime] = useState<number>();
  const [isTimeLocked, setIsTimeLocked] = useState<boolean>(false);
  const [isSmartCount, setIsSmartCount] = useState<boolean>(false);
  const [sliderValue, setSliderValue] = useState<number>(50);
  const [isActive, setIsActive] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const [hasRightToEdit, setHasRightToEdit] = useState(false);
  const [isCountZoneConfigEnabled, setisCountZoneConfigEnabled] = useState(false);
  const [stockCheckTypes, setStockCheckTypes] = useState<StockCheckTypesResponse[]>([]);
  const [stockCheckTypeId, setStockCheckTypeId] = useState<number | undefined>();

  const { id } = useParams();

  const isCreating = Number(id) <= 0 || id === undefined;

  const blocker = useBlocker(() => hasChanged && !hasCreated);

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  useEffect(() => {
    loadData();
    getCurrencyCode();
    getCountZonesWithItemsConfig();
    fetchStockCheckTypes();
    if (hasPermissionTo(['StockCountScheduleWrite'])) {
      setHasRightToEdit(true);
    }

    const fetchStrictEmailApproval = async () => {
      if (!selectedStore?.storeNumber || !selectedStore?.franchiseName) {
        setStrictEmailApprovalEnabled(null);
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await getConfiguration(ConfigurationName.StockCountStrictEmailApproval, selectedStore.storeNumber, selectedStore.franchiseName);
        let configValue: string | undefined = undefined;
        
        if (response?.data) {
          configValue = response.data.value;
        }
        setStrictEmailApprovalEnabled(configValue === 'true');
      } catch {
        setStrictEmailApprovalEnabled(false);
      }
    };
    fetchStrictEmailApproval();
  }, []);

  const getCurrencyCode = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.CurrencyCode, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      const code = new Intl.NumberFormat('en', { style: 'currency', currency: res.data?.value }).formatToParts(1)[0].value;
      setCurrencySymbol(code);
    });
  };

  const getCountZonesWithItemsConfig = () => {
    getConfiguration(ConfigurationName.CountZonesWithItemsEnabled, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((config: any) => {
        if (config.data.value === 'true') {
          setisCountZoneConfigEnabled(true);
        }
      })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any) => logError(e));
  };

  const fetchStockCheckTypes = () => {
    getStockCheckTypes()
      .then((res) => {
        setStockCheckTypes(res.data ?? []);
      })
      .catch((e) => logError(e));
  };

  // group schedules by multiStoresGroupId
  const groupSchedules = (schedules: StockCheckSchedule[]): InternalStockCheckSchedule[] => {
    const schedulesByMultiStoreGroup = schedules.reduce((acc, item) => {
      const groupId = item?.multiStoresGroupId;
      if (groupId) {
        acc[groupId] = acc[groupId] || [];
        acc[groupId].push(item);
      }
      return acc;
    }, {} as { [key: string]: StockCheckSchedule[] });

    const groupedSchedules: InternalStockCheckSchedule[] = Object.values(schedulesByMultiStoreGroup).map((groups) => ({
      ids: groups.map((item: StockCheckSchedule) => item.id) as number[],
      stockCheckScheduleDetailId: groups[0].stockCheckScheduleDetailId,
      storeNumbers: groups.map((item: StockCheckSchedule) => item.storeNumber) as string[],
      isActive: groups[0].isActive,
      generationDayOffset: groups[0].generationDayOffset,
      startDate: groups[0].startDate,
      recurrenceFrequency: groups[0].recurrenceFrequency,
      recurrenceInterval: groups[0].recurrenceInterval,
      scheduleType: groups[0].scheduleType,
      scheduledDayOfMonth: groups[0].scheduledDayOfMonth,
      stockCheckLastCreated: groups[0].stockCheckLastCreated,
      allStoresSelected: groups[0].allStoresSelected,
      multiStoresGroupId: groups[0].multiStoresGroupId,
    }));

    return groupedSchedules;
  };

  const loadData = () => {
    setIsLoading(true);
    getStockCheckScheduleByNumber(selectedStore?.storeNumber ?? '', Number(id ?? 0)).then((result) => {
      if (result.data) {
        setStores(result.data.stores ?? []);
        const StockCheckScheduleDetails = result.data;

        setCategoryData(StockCheckScheduleDetails.stockCheckSchedule?.categories ?? []);
        setProductTypeData(StockCheckScheduleDetails.stockCheckSchedule?.productTypes ?? []);
        setItemsData(StockCheckScheduleDetails.stockCheckSchedule?.items ?? []);
        setSchedules(groupSchedules(StockCheckScheduleDetails.stockCheckSchedule?.schedules ?? []));
        setCountZonesData(StockCheckScheduleDetails.stockCheckSchedule?.locations ?? []);

        setAllUsers(StockCheckScheduleDetails.approverEmails ?? []);
        setName(StockCheckScheduleDetails.stockCheckSchedule?.name ?? '');
        setSelectedUser(StockCheckScheduleDetails.stockCheckSchedule?.approver ?? '');
        setApprovalValue(StockCheckScheduleDetails.stockCheckSchedule?.toleranceValue);
        setSelectedTime(StockCheckScheduleDetails.stockCheckSchedule?.lockTillHour);
        setIsTimeLocked(StockCheckScheduleDetails.stockCheckSchedule?.lockTillCountDay ?? false);
        setIsSmartCount(StockCheckScheduleDetails.stockCheckSchedule?.isSmartCount ?? false);
        setSliderValue(StockCheckScheduleDetails.stockCheckSchedule?.tolerancePercentage ?? 0);
        setIsActive(StockCheckScheduleDetails.stockCheckSchedule?.isActive ?? false);
        setStockCheckTypeId(StockCheckScheduleDetails.stockCheckSchedule?.stockCheckType ?? -1);
      }
    })
      .catch(e => {
        logError(e);
        setSnackBarProps('error', `Error loading schedules: ${e}. please try again`);
      })
      .finally(() => setIsLoading(false));
  };

  const handleActiveChange = (event: SelectChangeEvent) => {
    setIsActive(!isActive);
    setHasChanged(true);
  };

  const handleIsTimeLockedChanged = (event: SelectChangeEvent) => {
    setIsTimeLocked(!isTimeLocked);
    setHasChanged(true);
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setSliderValue(newValue);
    }
    setHasChanged(true);
  };

  const handleCategories = (data: PrimaryCategoryStockCount[]) => {
    const convertedCategories = data.map(x => {
      return {
        primaryCategoryId: x.id,
        primaryCategoryName: x.primaryCategoryName,
        stockCheckScheduleDetailId: id ? Number(id) : 0,
      };
    });
    const categories = Array.from(new Set(categoryData.concat(convertedCategories)));
    setCategoryData(categories);
    setIsCategoriesModalOpen(false);
    setHasChanged(true);
  };

  const deleteCategories = () => {
    let data: PrimaryCategoryStockCount[] = [];
    if (categoryNameToDelete != undefined) {
      data = categoryData?.filter(item => item.primaryCategoryName !== categoryNameToDelete);
    }
    setCategoryData(data);
    handleClose();
    setHasChanged(true);
  };

  const handleProductTypes = (data: string[]) => {
    setProductTypeData(data);
    setIsProductTypeModalOpen(false);
    setHasChanged(true);
  }; 

  const deleteProductTypes = (data: string[]) => {
    setProductType('');
    setProductTypeData(data);
    setIsProductTypeDeleteModalOpen(false);
    setHasChanged(true);
  };

  const deleteItems = () => {
    let data: Item[] = [];
    if (itemNumberToDelete != undefined) {
      data = itemsData?.filter(item => item.itemNumber !== itemNumberToDelete);
    }
    setItemsData(data);
    handleClose();
    setHasChanged(true);
  };

  const deleteCountZones = () => {
    let data: typeof countZonesData = [];

    if (countZoneIdToDelete !== undefined) {
      data = countZonesData.filter(item => item.locationId !== countZoneIdToDelete);
    }

    setCountZonesData(data);
    if (!hasUnassignedZone(data)) {
      setItemsData([]);
    }
    handleClose();
    setHasChanged(true);
  };

  const areArraysEqual = (first: number[], second: number[]) => {
    if (first.length !== second.length) {
      return false;
    }

    for (let i = 0; i < first.length; i++) {
      if (first[i] !== second[i]) {
        return false;
      }
    }

    return true;
  };

  const confirmDeleteSchedule = () => {
    let data: InternalStockCheckSchedule[] = [];

    if (deleteAllSchedulesFlag === true) {
      data = [];
    } else if (scheduleToDeleteIndex !== undefined) {
      // remove non existing schedule items
      data = schedules?.filter((item, index) => index !== scheduleToDeleteIndex);
    } else {
      // remove existing schedule items
      data = schedules?.filter(item => !areArraysEqual(item.ids ?? [], scheduleIds ?? []));
    }
    setSchedules(data);
    handleClose();
    setHasChanged(true);
  };

  const deleteCategoryRow = (nameToDelete?: string) => {
    setCategoryNameToDelete(nameToDelete);
    setIsCategoryDeleteModalOpen(true);
  };

  const deleteProductTypeRow = (productType: string) => {
    setProductType(productType);
    setIsProductTypeDeleteModalOpen(true);
  };

  const deleteProductType = () => {
    setIsProductTypeDeleteModalOpen(true);
  };

  const deleteItemRow = (itemNumberToDelete?: string) => {
    setItemNumberToDelete(itemNumberToDelete);
    setIsDeleteModalOpen(true);
  };

  const deleteCountZoneRow = (idToDelete?: number) => {
    setCountZoneIdToDelete(idToDelete);
    setIsCountZonesDeleteModalOpen(true);
  };


  const deleteScheduleRow = (index?: number) => {
    if (index !== undefined) {
      const scheduleToDelete: InternalStockCheckSchedule = schedules[index];
      if (scheduleToDelete?.ids?.length !== 0) {
        // delete existing schedule row
        setScheduleIds(scheduleToDelete?.ids ?? []);
      } else {
        // delete non-existing schedule row
        setScheduleToDeleteIndex(index);
      }
    } else {
      // delete all schedule rows
      setDeleteAllSchedulesFlag(true);
    }

    setIsScheduleDeleteModalOpen(true);
  };

  const handleitems = (data: Item[]) => {
    const convertedData = data.filter(item => !itemsData.some(x => x.itemNumber === item.itemNumber)).map(item => {
      return {
        id: 0,
        stockCheckScheduleDetailId: undefined,
        itemNumber: item.itemNumber,
        description: item.description,
        category: item.primaryCategory,
        productType: item.productType,
        tolerancePercentage: 0,
        toleranceValue: 0,
      };
    });
    const items = Array.from(new Set(itemsData.concat(convertedData ?? [])));
    setItemsData(items);
    setHasChanged(true);
  };

  const handleCountZones = (data: CountZoneDto[]) => {
    const convertedZones = data
      .filter(zone => !countZonesData.some(x => x.locationId === zone.id))
      .map(zone => ({
        stockCheckScheduleDetailId: id ? Number(id) : 0,
        locationId: zone.id,
        name: zone.name,
        numberOfItems: zone.numberOfItems,
      }));

    const zones = Array.from(new Set(countZonesData.concat(convertedZones)));
    setCountZonesData(zones);
    if (zones.length > 0) {
      const allHaveItems = zones.every(z => (z.numberOfItems ?? 0) > 0);
      
      if (allHaveItems) {
        setProductTypeData([]);
        setCategoryData([]);
      }
      if (!hasUnassignedZone(zones)) {
        setItemsData([]);
      }
    }
    setIsCountZonesModalOpen(false);
    setHasChanged(true);
  };

  const handleClose = () => {
    setIsCategoriesModalOpen(false);
    setIsItemsModalOpen(false);
    setIsBulkModalOpen(false);
    setIsCategoryDeleteModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsScheduleDeleteModalOpen(false);
    setDeleteAllSchedulesFlag(false);
    setScheduleToDeleteIndex(undefined);
    setIsCountZonesModalOpen(false);
    setIsCountZonesDeleteModalOpen(false);
    setisCountZonesWithItemsModalOpen(false);
    setIsProductTypeModalOpen(false);
    setIsProductTypeDeleteModalOpen(false);
    setIsEndOfMonthCountModalOpen(false);
  };

  const handleAddCategoriesClick = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleProductTypeClickOpen = () => {
    setIsProductTypeModalOpen(true);
  };

  const handleAddItemsClick = () => {
    setIsItemsModalOpen(true);
  };

  const handleAddBulkItemClick = () => {
    setIsBulkModalOpen(true);
  };

  const handleAddCountZonesClick = () => {
    setIsCountZonesModalOpen(true);
  };

  const handleViewCountZoneItemsClick = (countZoneId: number) => {

    getCountZoneDetails(countZoneId)
      .then(response => {
        const data = response.data;
        if (!data) return;

        setcountZoneItemsData(data.items ?? []);
        setisCountZonesWithItemsModalOpen(true);
      })
      .catch(error => {
        logError(error);
      })
      .finally(() => setIsLoading(false));
  };


  const submitStock = () => {
    isCreating ? createStockSchedule() : updateStock();
  };

  const mapSchedules = (): StockCheckSchedule[] => {
    let multiStoresGroupId = 1;

    return schedules.flatMap((schedule: InternalStockCheckSchedule) => {
      multiStoresGroupId = schedule?.multiStoresGroupId ? schedule.multiStoresGroupId : multiStoresGroupId + 1;
      return (schedule.storeNumbers || []).map((store, index) => {
        const { storeNumbers, ...rest } = schedule;

        return {
          ...rest,
          id: schedule?.ids?.[index] ?? 0,
          storeNumber: store,
          multiStoresGroupId: schedule.multiStoresGroupId ? schedule.multiStoresGroupId : multiStoresGroupId + 1,
        };
      });
    });
  };

  const createStockSchedule = () => {
    const mappedSchedules: StockCheckSchedule[] = mapSchedules();

    const body: StockCheckScheduleDetail = {
      id: 0,
      name: name,
      franchise: selectedStore?.franchiseId,
      isActive: isActive,
      tolerancePercentage: sliderValue,
      toleranceValue: approvalValue,
      isSmartCount: isSmartCount,
      canMergeCount: true,
      lockTillCountDay: isTimeLocked,
      lockTillHour: selectedTime,
      updateSmartCountItems: false,
      approver: selectedUser,
      nextUpcomingCount: '',
      schedules: mappedSchedules,
      items: itemsData,
      categories: categoryData,
      productTypes: productTypeData,
      locations: countZonesData,
      stockCheckType: stockCheckTypeId,
    };

    setSnackBarProps('info', 'Creating schedule');
    setHasCreated(true);

    stockcheckschedulePOST(selectedStore?.storeNumber ?? '-1', body)
      .then(() => {
        setSnackBarProps('success', 'Created schedule');
        navigate('/stock-count-schedule/1');
      })
      .catch((error) => {
        if(error?.status === 409) {
          const parsedData = JSON.parse(error.response);
          const { status, existingCountId } = parsedData;
          setIsEndOfMonthCountModalOpen(true);
          setExistingStockCheckStatus(status);
          setExistingStockCheckId(existingCountId);
        }
        setSnackBarProps('error', t('errorMessage') + ' ' + error);
      });
  };

  const updateStock = () => {
    const mappedSchedules: StockCheckSchedule[] = mapSchedules();

    const body: StockCheckScheduleDetail = {
      id: Number(id),
      name: name,
      franchise: selectedStore?.franchiseId,
      isActive: isActive,
      tolerancePercentage: sliderValue,
      toleranceValue: approvalValue,
      isSmartCount: isSmartCount,
      canMergeCount: true,
      lockTillCountDay: isTimeLocked,
      lockTillHour: selectedTime,
      updateSmartCountItems: false,
      approver: selectedUser,
      nextUpcomingCount: '',
      schedules: mappedSchedules,
      items: itemsData,
      categories: categoryData,
      productTypes: productTypeData,
      locations: countZonesData,
      stockCheckType: stockCheckTypeId,
    };

    setSnackBarProps('info', 'Updating schedule');
    stockcheckschedulePOST(selectedStore?.storeNumber ?? '-1', body)
      .then(() => {
        setSnackBarProps('success', 'Updated schedule');
        setHasChanged(false);
        loadData();
      })
      .catch((error) => {
        if(error?.status === 409) {
          const parsedData = JSON.parse(error.response);
          const { status, existingCountId } = parsedData;
          setIsEndOfMonthCountModalOpen(true);
          setExistingStockCheckStatus(status);
          setExistingStockCheckId(existingCountId);
        }
        setSnackBarProps('error', t('errorMessage') + ' ' + error);
      });
  };

  const generateStock = () => {
    setSnackBarProps('info', 'Generating schedules');
    generatestockchecks(selectedStore?.storeNumber ?? '-1', Number(id))
      .then(() => {
        setSnackBarProps('success', 'Generated schedules');
      })
      .catch((error) => {
        if (error?.status === 409) {
          const parsedData = JSON.parse(error.response);
          const { status, existingCountId } = parsedData;

          setIsEndOfMonthCountModalOpen(true);
          setExistingStockCheckStatus(status);
          setExistingStockCheckId(existingCountId);
          return;
        }
        setSnackBarProps('error', `${t('errorMessage')} ${error}`);
      },
      );
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setHasChanged(true);
  };

  const handleSmartCountChange = () => {
    setIsSmartCount(!isSmartCount);
    setHasChanged(true);
  };

  const handleInputValue = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value, 10);
    if (!isNaN(newQuantity)) {
      setApprovalValue(newQuantity);
    } else {
      setApprovalValue(undefined);
    }
    setHasChanged(true);
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedTime = hour;
      options.push({
        value: formattedTime,
        label: `${String(hour).padStart(2, '0')}:00`,
      });
    }
    return options;
  };

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setSelectedTime(event.target.value as number | undefined);
    setHasChanged(true);
  };

  const handleUserSelect = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
    setHasChanged(true);
  };

  const handleLeaveClick = () => {
    navigate('/stock-count-schedule/1');
  };

  const handleValueChange = (itemNumber: string, newValue: string) => {
    let newNum = Number(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = itemsData.map((item) => {
      if (item.itemNumber === itemNumber) {
        return {
          ...item,
          toleranceValue: newNum,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const handleQtyChange = (itemNumber: string, newValue: string) => {
    let newNum = parseFloat(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = itemsData.map((item) => {
      if (item.itemNumber === itemNumber) {
        return {
          ...item,
          tolerancePercentage: newNum,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const handleCategoryChange = (id: number, newValue: string) => {
    const updatedItems = itemsData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          category: newValue,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  const handleStockCheckTypeChange = (event: SelectChangeEvent<number>) => {
    setStockCheckTypeId(Number(event.target.value));
    setHasChanged(true);
  };

  return {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckStatus,
    existingStockCheckId,
    allUsers,
    productType,
    handleActiveChange,
    handleQtyChange,
    handleValueChange,
    handleLeaveClick,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleAddBulkItemClick,
    handleAddItemsClick,
    handleAddCategoriesClick,
    handleProductTypeClickOpen,
    handleClose,
    handleitems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductType,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    today,
    selectedUser,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    itemsData,
    countZonesData,
    setCountZonesData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    schedules,
    stores,
    setSchedules,
    deleteScheduleRow,
    isScheduleDeleteModalOpen,
    confirmDeleteSchedule,
    handleSmartCountChange,
    isSmartCount,
    handleCategoryChange,
    isActive,
    isLoading,
    generateStock,
    isCreating,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    currencySymbol,
    hasRightToEdit,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    handleCountZones,
    deleteCountZoneRow,
    isCountZonesDeleteModalOpen,
    deleteCountZones,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    handleStockCheckTypeChange,
    stockCheckTypes,
    stockCheckTypeId,
    strictEmailApprovalEnabled,
  };
};
